import type { IChatListItem } from '@/apiClient/types/chat'

/**
 * The pinned chats plus the dated ones, newest first.
 *
 * `GET /chats` returns pinned chats beside `items` rather than inside it, so anything
 * reading the list as "all my chats" — the search modal — has to merge them back or a
 * pinned chat becomes the one chat the user cannot search for. Pinned rows are filtered
 * out of `chats` as well as prepended, so a row the server still lists while an
 * optimistic pin is in flight cannot appear twice.
 */
export function mergePinnedIntoChatList(
  pinned: IChatListItem[],
  chats: IChatListItem[],
): IChatListItem[] {
  const pinnedIds = new Set(pinned.map((chat) => chat.id))
  return [...pinned, ...chats.filter((chat) => !pinnedIds.has(chat.id))].sort(
    (a, b) => b.updated_at - a.updated_at,
  )
}

/**
 * The pinned list the user wants after pinning or unpinning `chat`, preserving the order
 * of everything else. `PATCH /chats/pins` replaces the whole list, so a caller has to
 * send the complete result rather than a delta — appending puts a newly pinned chat last.
 *
 * Returns the list unchanged when the chat is already in the requested state, so a double
 * submit cannot append a duplicate (which the API rejects with 422).
 */
export function withChatPinned(
  pinned: IChatListItem[],
  chat: IChatListItem,
  isPinned: boolean,
): IChatListItem[] {
  const alreadyPinned = pinned.some((pinnedChat) => pinnedChat.id === chat.id)

  if (isPinned) return alreadyPinned ? pinned : [...pinned, chat]
  if (!alreadyPinned) return pinned
  return pinned.filter((pinnedChat) => pinnedChat.id !== chat.id)
}

/**
 * The chats the sidebar's dated "Chats" list should render.
 *
 * `GET /chats` keeps pinned chats out of `items` because they have their own section
 * above it. Switching pinning off takes that section away, so they have to be folded
 * back into the dated list — otherwise turning off a flag would make a user's pinned
 * chats disappear from the sidebar entirely rather than merely lose their heading.
 */
export function selectDatedChats(
  isPinningEnabled: boolean,
  pinned: IChatListItem[],
  chats: IChatListItem[],
): IChatListItem[] {
  if (isPinningEnabled) return chats
  return mergePinnedIntoChatList(pinned, chats)
}
